import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import ClarityFormsPicker from './ClarityFormsPicker';

const RUN_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/runClarityReport';

// Latest + archive of the monthly Core6 Clarity (call intelligence) PDFs for a
// client. Reads the clients/{id}/clarityReports subcollection the scheduler
// writes — never the callscaler_api_key. "Run Now" calls the worker for the
// prior month with a staff token (manual single-client trigger).
export default function ClarityReportsPanel({ clientId, client }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null);

  useEffect(() => {
    const ref = collection(db, 'clients', clientId, 'clarityReports');
    const unsub = onSnapshot(ref, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Newest period first (doc id is YYYY-MM, so string sort is chronological)
      data.sort((a, b) => (b.period || '').localeCompare(a.period || ''));
      setReports(data);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [clientId]);

  const fmtGenerated = (ts) => {
    const d = ts?.toDate ? ts.toDate() : null;
    if (!d) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const runNow = async () => {
    setRunning(true);
    setRunMsg(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(RUN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId, force: true }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        const formsText = data.formsProcessed ? `, ${data.formsProcessed} form leads` : '';
        setRunMsg({ ok: true, text: `✓ Generated ${data.periodLabel || ''} — ${data.callsProcessed} calls${formsText}` });
      } else if (data.status === 'skipped') {
        setRunMsg({ ok: true, text: data.reason === 'no_calls' ? 'No calls found for the prior month — nothing to report.' : 'Already generated.' });
      } else {
        setRunMsg({ ok: false, text: data.error || 'Report generation failed.' });
      }
    } catch (e) {
      setRunMsg({ ok: false, text: 'Could not reach the report function. Try again.' });
    } finally {
      setRunning(false);
    }
  };

  const latest = reports[0];
  const archive = reports.slice(1);
  const enabled = client?.clarity_enabled;
  const hasKey = !!client?.callscaler_api_key;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Core6 Clarity Reports</div>
        <button className="btn btn-secondary btn-sm" onClick={runNow} disabled={running || !hasKey}>
          {running ? 'Running…' : 'Run Now'}
        </button>
      </div>

      {!enabled && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Monthly auto-generation is <strong>disabled</strong>. Enable it in Content Settings to run on the 1st of each month.
          {!hasKey && ' Add a CallScaler API key first.'}
        </div>
      )}
      {enabled && !hasKey && (
        <div style={{ fontSize: 12, color: '#f59e0b', marginBottom: 12 }}>
          ⚠ No CallScaler API key set — add one in Content Settings or reports will fail.
        </div>
      )}

      {runMsg && (
        <div style={{ fontSize: 12, marginBottom: 12, color: runMsg.ok ? '#4ade80' : '#f87171' }}>{runMsg.text}</div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading reports…</div>
      ) : !latest ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          No reports yet. {hasKey ? 'Use “Run Now” to generate the prior month.' : ''}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 }}>Latest Report</div>
          <ReportRow report={latest} fmtGenerated={fmtGenerated} highlight />

          {archive.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', margin: '18px 0 8px' }}>Report Archive</div>
              {archive.map(r => <ReportRow key={r.id} report={r} fmtGenerated={fmtGenerated} />)}
            </>
          )}
        </>
      )}

      {enabled && <ClarityFormsPicker clientId={clientId} />}
    </div>
  );
}

function ReportRow({ report, fmtGenerated, highlight }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '10px 12px', borderRadius: 8, marginBottom: 8,
        background: highlight ? '#0a1f12' : 'var(--bg-primary)',
        border: '1px solid', borderColor: highlight ? '#16a34a55' : 'var(--border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
          {report.periodLabel || report.period}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {report.callsProcessed != null ? `${report.callsProcessed} calls` : ''}
          {report.forms?.metrics?.leads ? ` · ${report.forms.metrics.leads} form leads` : ''}
          {report.generatedAt ? ` · Generated ${fmtGenerated(report.generatedAt)}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <a className="btn btn-secondary btn-sm" href={report.url} target="_blank" rel="noreferrer">View</a>
        <a className="btn btn-secondary btn-sm" href={report.url} download={`clarity-${report.period}.pdf`}>Download</a>
      </div>
    </div>
  );
}
